import React from 'react';

import { type ColorToken } from '@backrs_ui/common-ui';

import { Box, Chip, SxProps, Theme } from '@mui/material';

import { GuideMarkdown } from './GuideMarkdown';

export type GuideDefinitionTone = 'brand' | 'success' | 'orange' | 'yellow';

export type GuideDefinition = Readonly<{
  term: string;
  /** Markdown definition copy. */
  body: string;
  tone?: GuideDefinitionTone;
}>;

type GuideDefinitionStripProps = Readonly<{
  definitions: readonly GuideDefinition[];
}>;

/** Terms a guide section relies on, each as a colored chip beside its definition. */
export const GuideDefinitionStrip: React.FC<GuideDefinitionStripProps> = ({ definitions }) => (
  <Box sx={stripSx}>
    {definitions.map((definition) => (
      <Box key={definition.term} sx={definitionSx}>
        <Chip
          label={definition.term}
          variant="tag"
          size="sm"
          sx={chipSx(definition.tone ?? 'brand')}
        />
        <GuideMarkdown variant="text-sm-regular">{definition.body}</GuideMarkdown>
      </Box>
    ))}
  </Box>
);

const TONE_COLORS: Record<
  GuideDefinitionTone,
  Readonly<{ background: ColorToken; border: ColorToken; text: ColorToken }>
> = {
  brand: { background: 'Brand/50', border: 'Brand/200', text: 'Colors/Text/text-brand-primary' },
  success: { background: 'Success/50', border: 'Success/200', text: 'Success/700' },
  orange: { background: 'Orange/50', border: 'Orange/200', text: 'Orange/700' },
  yellow: { background: 'Yellow/50', border: 'Yellow/200', text: 'Yellow/700' },
};

const stripSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const definitionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-md'],
});

const chipSx =
  (tone: GuideDefinitionTone): SxProps<Theme> =>
  ({ backrs }) => {
    const { background, border, text } = TONE_COLORS[tone];

    return {
      height: 'auto',
      flex: '0 0 auto',
      px: backrs.spacing['spacing-sm'],
      py: backrs.spacing['spacing-xxs'],
      border: `1px solid ${backrs.colors[border]}`,
      backgroundColor: backrs.colors[background],
      color: backrs.colors[text],
    };
  };
